import type { CSSProperties, FC } from 'react';
import type { FormatStyleName } from 'javascript-time-ago';

import { useUserStore } from '@/stores/database/entities/00-RBAC/01-Users/store';
import { useWorkspaceStore } from '@/stores/database/entities/00-RBAC/02-Workspaces/store';
import { formatDate, formatPreferredTimezoneDateTime } from '@/utilities';

import SmallTooltip from '../../tooltips/SmallTooltip';
import type { SmallTooltipProps } from '../../tooltips/SmallTooltip';

interface RelativeTimeTooltipProps {
    date?: Date | number | string;
    formatStyle?: FormatStyleName;
    placement?: SmallTooltipProps['placement'];
    style?: CSSProperties;
}

/** Compact relative time with an exact timestamp localized to the viewer's preferred timezone. */
const RelativeTimeTooltip: FC<RelativeTimeTooltipProps> = ({
    date,
    formatStyle = 'mini',
    placement = 'top',
    style,
}) => {
    const personalTimezone = useUserStore(
        (state) => state.selectedUser?.timeZone ?? state.selectedUser?.activeMember?.user?.timeZone,
    );
    const workspaceTimezone = useWorkspaceStore((state) => state.selectedWorkspace?.workspaceTimeZone);
    const parsedDate = date === undefined ? undefined : new Date(date instanceof Date ? date.getTime() : date);
    const validDate = parsedDate && !Number.isNaN(parsedDate.getTime()) ? parsedDate : undefined;
    const tooltipTitle = validDate
        ? formatPreferredTimezoneDateTime(validDate, personalTimezone ?? workspaceTimezone, { weekday: 'long' })
        : undefined;

    return (
        <SmallTooltip placement={placement} showInfo={false} title={tooltipTitle}>
            <time
                dateTime={validDate?.toISOString()}
                data-relative-time-tooltip='true'
                style={{ display: 'inline', ...style }}
            >
                {validDate ? formatDate(validDate, formatStyle) : '—'}
            </time>
        </SmallTooltip>
    );
};

export type { RelativeTimeTooltipProps };
export default RelativeTimeTooltip;
